import React from 'react';
import type { DesignElementDepth, OverlayTemplateConfig } from '../../../api/tendApi';
import ColorPickerPopover from '../ColorPickerPopover';
import FontPickerPopover from './FontPickerPopover';
import FontSizeInput from './FontSizeInput';
import TextStylePopover, { TextCase } from './TextStylePopover';
import ElementDesignPopover from './ElementDesignPopover';
import GapControls from './GapControls';
import MiniPositionPicker from './MiniPositionPicker';
import { PopoverGroup } from './PopoverGroup';
import { ToolbarButton } from './ToolbarButton';
import OpacityControl from './OpacityControl';
import type { ToolbarPlacement } from './ElementFrame';

export { ToolbarButton, TOOLBAR_BOX } from './ToolbarButton';

export type TextElementKind = 'title' | 'subheader' | 'body';

/** Everything the text toolbar shows for a title / subheader / body. */
export interface TextToolbarValue {
  textColor: string;
  fontFamily: string | null;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  shadow: boolean;
  textCase: TextCase;
  letterSpacing: number;
  lineHeight: number | null;
  bgEnabled: boolean;
  bgColor: string;
  bgOpacity: number;
  borderEnabled: boolean;
  borderColor: string;
  borderThickness: number;
  borderRadius: number;
  /** Visible space below this element to the next one (reference px, floor 0). */
  gap: number;
  position: string;
}

interface TextToolbarProps {
  kind: TextElementKind;
  value: TextToolbarValue;
  onChange: (patch: Partial<TextToolbarValue>) => void;
  fontMin?: number;
  lineHeightAutoSeed?: number;
  /** Config the font picker reads inherited families from (template editor). */
  fontInheritConfig?: Partial<OverlayTemplateConfig> | null;
}

interface CategoryToolbarProps {
  kind: 'category';
  borderRadius: number;
  bgColor: string;
  fontFamily: string | null;
  position: string;
  onBorderRadiusPreview?: (value: number) => void;
  onBorderRadiusCommit: (value: number) => void;
  onBgColorPreview: (color: string) => void;
  onBgColorCommit?: (color: string) => void;
  onFontFamilyChange: (slug: string | null) => void;
  onPositionChange: (pos: string) => void;
  /** Visible space below the pill to the next element (reference px, floor 0). */
  gap: number;
  onGapChange: (gap: number) => void;
}

interface LogoToolbarProps {
  kind: 'logo';
  position: string;
  onPositionChange: (pos: string) => void;
  /** Visible space below the logo to the next element (reference px, floor 0). */
  gap: number;
  onGapChange: (gap: number) => void;
  onChangeLogo?: () => void;
  /** Template editor's "set / default" marker. */
  badge?: React.ReactNode;
}

/** What the design-element pill can change (Depth Ordering of design element, 2026-10-08). */
export interface DesignToolbarPatch {
  depth?: DesignElementDepth;
  flipX?: boolean;
  /** 0–100, what the slider shows. */
  opacity?: number;
}

interface DesignToolbarProps {
  kind: 'design';
  /** 'back' = behind the set elements (above gradient + border); 'front' = the top-most layer. */
  depth: DesignElementDepth;
  flipX: boolean;
  /** 0–100. */
  opacity: number;
  onChange: (patch: DesignToolbarPatch) => void;
}

export type ElementToolbarProps = (TextToolbarProps | CategoryToolbarProps | LogoToolbarProps | DesignToolbarProps) & {
  /** Where the frame put the toolbar; popovers open away from the element. */
  placement: ToolbarPlacement;
};

const SHELL = 'flex flex-nowrap items-center gap-1 bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5 [&>*]:shrink-0';

/**
 * The per-element toolbar, one component with a control set per element
 * kind, used by the card and the template editors alike. Lives in the
 * ElementFrame's toolbar slot. Its popovers are mutually exclusive.
 * Toggle-off is the frame's X, not a toolbar button.
 */
const ElementToolbar: React.FC<ElementToolbarProps> = (props) => {
  const { placement } = props;
  const popoverPosition: 'above' | 'below' = placement;

  let controls: React.ReactNode;

  if (props.kind === 'category') {
    const p = props;
    controls = (
      <>
        <input
          type="range"
          min="0"
          max="100"
          value={p.borderRadius}
          onChange={(e) => p.onBorderRadiusPreview?.(parseInt(e.target.value, 10))}
          onPointerUp={(e) => p.onBorderRadiusCommit(parseInt(e.currentTarget.value, 10))}
          onKeyUp={(e) => p.onBorderRadiusCommit(parseInt(e.currentTarget.value, 10))}
          onClick={(e) => e.stopPropagation()}
          className="w-12 h-1 accent-blue-500 cursor-pointer"
          title="Corner rounding"
          aria-label="Corner rounding"
        />
        <ColorPickerPopover
          color={p.bgColor}
          onChange={p.onBgColorPreview}
          onChangeCommitted={p.onBgColorCommit}
          triggerSize="sm"
          position={popoverPosition}
          title="Category background color"
        />
        <FontPickerPopover
          value={p.fontFamily}
          onChange={p.onFontFamilyChange}
          element="category"
          position={popoverPosition}
        />
        <GapControls value={p.gap} onChange={p.onGapChange} />
        <MiniPositionPicker currentPosition={p.position} onChange={p.onPositionChange} />
      </>
    );
  } else if (props.kind === 'logo') {
    const p = props;
    controls = (
      <>
        {/* Size lives on the frame's corner handle (ticket: Category/Logo selection & resize). */}
        {p.onChangeLogo && (
          <ToolbarButton title="Change logo" onClick={() => p.onChangeLogo?.()}>
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </ToolbarButton>
        )}
        {p.badge}
        <GapControls value={p.gap} onChange={p.onGapChange} />
        <MiniPositionPicker currentPosition={p.position} onChange={p.onPositionChange} />
      </>
    );
  } else if (props.kind === 'design') {
    const p = props;
    const behind = p.depth === 'back';
    // The same boxed controls as the text pill. Front/back is a toggle whose
    // ON state is "behind the text" and whose label names the move it would
    // make; the filled square in the icon is where the element goes. Flip
    // mirrors left↔right only (top↔bottom is flip + the rotate handle at
    // 180°). Opacity is the house slider in a box of the same height with a
    // typed number beside it (OpacityControl), live per tick like a drag —
    // both editors keep placements in local state.
    const depthLabel = behind ? 'Bring in front of the text' : 'Send behind the text';
    controls = (
      <>
        <ToolbarButton title={depthLabel} toggle active={behind} onClick={() => p.onChange({ depth: behind ? 'front' : 'back' })} dataAttrs={{ 'data-design-depth': p.depth }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            {behind ? (
              <>
                <rect x="4" y="4" width="11" height="11" rx="1" />
                <rect x="9" y="9" width="11" height="11" rx="1" fill="currentColor" />
              </>
            ) : (
              <>
                <rect x="4" y="4" width="11" height="11" rx="1" fill="currentColor" />
                <rect x="9" y="9" width="11" height="11" rx="1" />
              </>
            )}
          </svg>
        </ToolbarButton>
        <ToolbarButton title="Flip left ↔ right" toggle active={p.flipX} onClick={() => p.onChange({ flipX: !p.flipX })}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round">
            <path d="M12 3v18" strokeDasharray="2 2" />
            <path d="M10 6L4 18h6z" fill={p.flipX ? 'none' : 'currentColor'} />
            <path d="M14 6l6 12h-6z" fill={p.flipX ? 'currentColor' : 'none'} />
          </svg>
        </ToolbarButton>
        <OpacityControl value={p.opacity} onChange={(n) => p.onChange({ opacity: n })} />
      </>
    );
  } else {
    const { kind, value: v, onChange, fontMin = kind === 'title' ? 10 : 8, lineHeightAutoSeed = kind === 'title' ? 1.25 : 1.375, fontInheritConfig = null } = props;
    controls = (
      <>
        <ColorPickerPopover
          color={v.textColor}
          onChange={(c) => onChange({ textColor: c })}
          triggerSize="tb"
          position={popoverPosition}
          title="Text color"
        />
        <FontPickerPopover
          value={v.fontFamily}
          onChange={(slug) => onChange({ fontFamily: slug })}
          element={kind}
          inheritConfig={fontInheritConfig}
          position={popoverPosition}
        />
        <FontSizeInput value={v.fontSize} onChange={(n) => onChange({ fontSize: n })} min={fontMin} />
        <TextStylePopover
          value={{
            bold: v.bold,
            italic: v.italic,
            underline: v.underline,
            strikethrough: v.strikethrough,
            shadow: v.shadow,
            textCase: v.textCase,
            letterSpacing: v.letterSpacing,
            lineHeight: v.lineHeight,
          }}
          onChange={(next) => onChange(next)}
          lineHeightAutoSeed={lineHeightAutoSeed}
          position={popoverPosition}
        />
        <ElementDesignPopover
          value={{
            bgEnabled: v.bgEnabled,
            bgColor: v.bgColor,
            bgOpacity: v.bgOpacity,
            borderEnabled: v.borderEnabled,
            borderColor: v.borderColor,
            borderThickness: v.borderThickness,
            borderRadius: v.borderRadius,
          }}
          onChange={(next) => onChange(next)}
          position={popoverPosition}
        />
        <GapControls value={v.gap} onChange={(n) => onChange({ gap: n })} />
        <MiniPositionPicker currentPosition={v.position} onChange={(pos) => onChange({ position: pos })} />
      </>
    );
  }

  return (
    <PopoverGroup>
      <div className={SHELL} data-overlay-toolbar-kind={props.kind} role="toolbar" aria-label={`${props.kind} controls`}>
        {controls}
      </div>
    </PopoverGroup>
  );
};

export default ElementToolbar;
